import {
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    CacheType,
    createObservableDataAction,
    IGeneric,
    IAny,
    getCatalogId
} from '@msdyn365-commerce/core';
import { Category, Product, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { searchAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getCategoriesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CategoriesDataActions.g';

import { isValidRootCategory } from '../common/config/category-constants';

// Paramètres optionnels
export interface IProductRootCategoriesParams {
    productRecordId?: number;
    channelId?: number;
    catalogId?: number;
}

export interface IProductRootCategories {
    rootCategories: Category[];
    productCategories: Category[];
}

export class GetProductRootCategoriesInput implements IActionInput {
    public productRecordId: number;
    public channelId: number;
    public catalogId?: number;

    constructor(productRecordId: number, channelId: number, catalogId?: number) {
        this.productRecordId = productRecordId;
        this.channelId = channelId;
        this.catalogId = catalogId;
    }

    public getCacheKey(): string {
        return `ProductRootCategories-${this.productRecordId}-${this.channelId}-${this.catalogId || 'default'}`;
    }

    public getCacheObjectType(): string {
        return 'ProductRootCategories';
    }

    public dataCacheType(): CacheType {
        return 'request';
    }
}

const getProductRootCategoriesAction = async (
    input: IActionInput | IActionInput[],
    ctx: IActionContext
): Promise<IProductRootCategories> => {
    const typedInput = input as GetProductRootCategoriesInput;

    // console.log('🚀 ProductRootCategories action started with searchAsync approach:', {
    //     productRecordId: typedInput.productRecordId,
    //     channelId: typedInput.channelId,
    //     catalogId: typedInput.catalogId
    // });

    try {
        // 0. Vérifier si on a des paramètres valides
        if (!typedInput.productRecordId || typedInput.productRecordId === 0) {
            // console.log('⚠️ No valid productRecordId, returning empty results');
            return {
                rootCategories: [],
                productCategories: []
            };
        }

        // 1. Récupérer le produit avec searchAsync
        // console.log('📦 Fetching product with searchAsync...');

        let productResult: Product;
        try {
            // Créer les critères de recherche pour un produit spécifique
            const productSearchCriteria: ProductSearchCriteria = {
                Context: {
                    ChannelId: typedInput.channelId,
                    CatalogId: typedInput.catalogId || 0
                },
                Ids: [typedInput.productRecordId]
            };

            // console.log('🔍 Search criteria:', {
            //     ChannelId: productSearchCriteria.Context?.ChannelId,
            //     CatalogId: productSearchCriteria.Context?.CatalogId,
            //     ProductIds: productSearchCriteria.Ids
            // });

            const productsArray = await searchAsync({ callerContext: ctx }, productSearchCriteria);

            if (productsArray && productsArray.length > 0) {
                productResult = productsArray[0];
                // console.log('✅ Product searchAsync successful:', {
                //     RecordId: productResult.RecordId,
                //     Name: productResult.Name,
                //     CategoryIds: productResult.CategoryIds,
                //     ItemId: productResult.ItemId,
                //     Description: productResult.Description
                // });
                // console.log('📦 Product:', productResult);
            } else {
                // console.log('⚠️ Product not found in search results');
                return {
                    rootCategories: [],
                    productCategories: []
                };
            }
        } catch (productError) {
            // console.error('❌ Exception in product searchAsync:', productError);
            ctx.trace(`Unable to Fetch Product with searchAsync: ${productError}`);
            return {
                rootCategories: [],
                productCategories: []
            };
        }

        if (!productResult || !productResult.CategoryIds) {
            // console.log('⚠️ Product has no CategoryIds');
            return {
                rootCategories: [],
                productCategories: []
            };
        }

        const productCategoryIds = productResult.CategoryIds || [];

        if (productCategoryIds.length === 0) {
            // console.log('⚠️ Product has empty CategoryIds array');
            return {
                rootCategories: [],
                productCategories: []
            };
        }

        // console.log('📂 Product belongs to categories:', productCategoryIds);

        // 2. Récupérer toutes les catégories avec getCategoriesAsync
        // console.log('🔍 Fetching categories with getCategoriesAsync...');

        let allCategories: Category[];
        try {
            allCategories = await getCategoriesAsync({ callerContext: ctx }, typedInput.channelId, typedInput.catalogId);

            // console.log('✅ Categories getCategoriesAsync successful:', allCategories.length, 'categories found');
            // console.log(
            //     '📂 Categories:',
            //     allCategories.map(cat => ({
            //         RecordId: cat.RecordId,
            //         Name: cat.Name,
            //         ParentCategory: cat.ParentCategory
            //     }))
            // );
            //console.log('All Categories (complete):', JSON.stringify(allCategories, null, 2));
        } catch (categoriesError) {
            // console.error('❌ Exception in categories getCategoriesAsync:', categoriesError);
            ctx.trace(`Unable to Fetch Categories with getCategoriesAsync: ${categoriesError}`);
            return {
                rootCategories: [],
                productCategories: []
            };
        }

        if (!allCategories || allCategories.length === 0) {
            // console.log('❌ No categories found or invalid response');
            return {
                rootCategories: [],
                productCategories: []
            };
        }

        // console.log('✅ Categories validation passed, processing', allCategories.length, 'categories');

        // Vérifier si les catégories du produit existent dans la liste des catégories
        const existingCategoryIds = allCategories.map(cat => cat.RecordId).filter(Boolean);
        const missingCategoryIds = productCategoryIds.filter(id => !existingCategoryIds.includes(id));

        if (missingCategoryIds.length > 0) {
            console.warn("⚠️ Product has categories that don't exist:", missingCategoryIds);
        }

        // 3. Récupérer les catégories du produit (seulement celles qui existent)
        const productCategories = allCategories.filter(cat => cat.RecordId && productCategoryIds.includes(cat.RecordId));

        // console.log(
        //     '📂 Valid product categories found:',
        //     productCategories.map(cat => ({
        //         RecordId: cat.RecordId,
        //         Name: cat.Name,
        //         ParentCategory: cat.ParentCategory
        //     }))
        // );

        // 4. Fonction pour remonter à la pseudo-racine (premier niveau sous la racine absolue)
        const findPseudoRootCategory = (categoryId: number): Category | null => {
            const category = allCategories.find(cat => cat.RecordId === categoryId);
            if (!category) {
                // console.warn('⚠️ Category not found:', categoryId);
                return null;
            }

            // Si la catégorie n'a pas de parent, c'est la racine absolue (Rejo) - pas ce qu'on veut
            if (!category.ParentCategory || category.ParentCategory === 0) {
                // console.log('🌳 Absolute root category found (Rejo):', { RecordId: category.RecordId, Name: category.Name });
                return null; // On ne veut pas la racine absolue
            }

            // Trouver le parent pour vérifier s'il est la racine absolue
            const parentCategory = allCategories.find(cat => cat.RecordId === category.ParentCategory);
            if (!parentCategory) {
                console.warn('⚠️ Parent category not found:', category.ParentCategory);
                return null;
            }

            // Si le parent n'a pas de parent, alors notre catégorie actuelle est un pseudo-root
            if (!parentCategory.ParentCategory || parentCategory.ParentCategory === 0) {
                // console.log('🎯 Pseudo-root category found:', {
                //     RecordId: category.RecordId,
                //     Name: category.Name,
                //     Parent: parentCategory.Name
                // });
                // Vérifier que c'est bien Products ou Recipes avec l'enum
                if (isValidRootCategory(category.Name)) {
                    return category;
                }
                console.warn('⚠️ Unknown pseudo-root category:', category.Name);
                return null;
            }

            // Sinon, continuer à remonter
            // console.log('⬆️ Moving up from category:', {
            //     RecordId: category.RecordId,
            //     Name: category.Name,
            //     ParentCategory: category.ParentCategory
            // });
            return findPseudoRootCategory(category.ParentCategory);
        };

        // 5. Déterminer les pseudo-catégories racines (Products et Recipes)
        const productPseudoRootCategories: Category[] = [];
        const seenPseudoRootIds = new Set<number>();

        for (const categoryId of productCategoryIds) {
            // Vérifier si la catégorie existe avant de chercher sa pseudo-racine
            if (!existingCategoryIds.includes(categoryId)) {
                // console.warn('⚠️ Skipping non-existent category:', categoryId);
                continue;
            }

            const pseudoRootCategory = findPseudoRootCategory(categoryId);
            if (pseudoRootCategory && pseudoRootCategory.RecordId && !seenPseudoRootIds.has(pseudoRootCategory.RecordId)) {
                productPseudoRootCategories.push(pseudoRootCategory);
                seenPseudoRootIds.add(pseudoRootCategory.RecordId);
                // console.log('✅ Added pseudo-root category:', {
                //     RecordId: pseudoRootCategory.RecordId,
                //     Name: pseudoRootCategory.Name
                // });
            }
        }

        const result = {
            rootCategories: productPseudoRootCategories, // Maintenant ce sont les pseudo-roots
            productCategories: productCategories
        };

        // console.log('🎉 ProductRootCategories action completed successfully:', {
        //     pseudoRootCategoriesCount: result.rootCategories.length,
        //     productCategoriesCount: result.productCategories.length,
        //     pseudoRootCategories: result.rootCategories.map(cat => ({ RecordId: cat.RecordId, Name: cat.Name }))
        // });

        return result;
    } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        ctx.telemetry.exception(errorObj);
        // console.error('💥 Error in ProductRootCategories action:', error);
        return {
            rootCategories: [],
            productCategories: []
        };
    }
};

// createInput function
const createInput = (args: ICreateActionContext, customParams?: IProductRootCategoriesParams): GetProductRootCategoriesInput => {
    try {
        // console.log('🔧 ProductRootCategories createInput started');
        const finalProductRecordId = customParams?.productRecordId || parseInt(getSelectedProductIdFromActionInput(args) || '0', 10);

        // Extraction du channelId et catalogId
        const channelId =
            customParams?.channelId ||
            (args.requestContext?.apiSettings?.channelId ? parseInt(args.requestContext.apiSettings.channelId.toString(), 10) : 0);

        // Extraction du catalogId avec priorité à getCatalogId
        let catalogId =
            getCatalogId(args.requestContext) ||
            customParams?.catalogId ||
            (args.requestContext?.apiSettings?.catalogId ? parseInt(args.requestContext.apiSettings.catalogId.toString(), 10) : 0);

        // Fallback: récupérer catalogId depuis l'URL si présent
        if (!catalogId && args.requestContext?.query?.catalogid) {
            catalogId = parseInt(args.requestContext.query.catalogid, 10);
        }

        // console.log('✅ ProductRootCategories createInput completed:', {
        //     finalProductRecordId,
        //     channelId,
        //     catalogId
        // });

        return new GetProductRootCategoriesInput(finalProductRecordId, channelId, catalogId);
    } catch (error) {
        console.error('💥 Error in ProductRootCategories createInput:', error);
        return new GetProductRootCategoriesInput(0, 0, 0);
    }
};

/**
 * Utility function to extract the active productId in the following priority:
 * First query param (productId), then UrlToken (itemId), then module config.
 * @param inputData - The Action Input data.
 * @returns Productid or undefined.
 */
export const getSelectedProductIdFromActionInput = (inputData: ICreateActionContext<IGeneric<IAny>>): string | undefined => {
    const context = inputData.requestContext;
    if (context.query?.productId) {
        return context.query.productId;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Exsiting code.
    } else if (
        context.urlTokens &&
        inputData.requestContext.urlTokens.itemId &&
        inputData.requestContext.urlTokens.pageType === 'Product'
    ) {
        return context.urlTokens.itemId;
    } else if (inputData && inputData.config && inputData.config.productId) {
        return inputData.config.productId;
    }
    return undefined;
};

// Fonction helper pour les appels manuels
export const callProductRootCategoriesAction = async (
    productRecordId: number,
    channelId: number,
    catalogId: number | undefined,
    actionContext: IActionContext
): Promise<IProductRootCategories> => {
    const input = new GetProductRootCategoriesInput(productRecordId, channelId, catalogId);
    return getProductRootCategoriesAction(input, actionContext);
};

export default createObservableDataAction({
    action: <IAction<IProductRootCategories>>getProductRootCategoriesAction,
    input: createInput
});
